<template>
  <div :class="['filled-tab', tabCountClass, { 'filled-tab--mobile-full': equalWidthOnMobile }]">
    <div class="filled-tab__tabs" role="tablist" aria-label="コンテンツ切り替えタブ">
      <button
        v-for="tab in normalizedTabs"
        :id="`filled-tab-${tab.id}`"
        :key="tab.id"
        type="button"
        class="filled-tab__tab"
        :class="{ 'is-active': tab.id === activeId }"
        role="tab"
        :aria-selected="tab.id === activeId"
        :aria-controls="`filled-tab-panel-${tab.id}`"
        @click="setActive(tab.id)"
      >
        <span class="filled-tab__label">{{ tab.label }}</span>
      </button>
    </div>

    <div
      v-if="activeTab"
      :id="`filled-tab-panel-${activeTab.id}`"
      class="filled-tab__panel"
      role="tabpanel"
      :aria-labelledby="`filled-tab-${activeTab.id}`"
    >
      <slot :active-tab="activeTab">
        <div class="filled-tab__placeholder">
          <p class="filled-tab__placeholder-title">{{ activeTab.label }}</p>
          <p class="filled-tab__placeholder-text">コンテンツをここに配置してください。</p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { FilledTabItem, FilledTabProps } from './types';

const props = withDefaults(defineProps<FilledTabProps>(), {
  equalWidthOnMobile: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
}>();

const normalizedTabs = computed<FilledTabItem[]>(() => props.tabs || []);

const resolveInitialId = () => {
  if (props.modelValue && normalizedTabs.value.some((tab) => tab.id === props.modelValue)) {
    return props.modelValue;
  }
  return normalizedTabs.value[0]?.id ?? '';
};

const internalId = ref<string>(resolveInitialId());

watch(
  () => props.modelValue,
  (next) => {
    if (next && normalizedTabs.value.some((tab) => tab.id === next)) {
      internalId.value = next;
    }
  }
);

watch(
  normalizedTabs,
  (tabs) => {
    if (!tabs.some((tab) => tab.id === internalId.value)) {
      internalId.value = tabs[0]?.id ?? '';
    }
  },
  { deep: true }
);

const activeId = computed(() => internalId.value);
const activeTab = computed(() => normalizedTabs.value.find((tab) => tab.id === activeId.value));
const tabCountClass = computed(() => {
  switch (normalizedTabs.value.length) {
    case 2:
      return 'filled-tab--double';
    case 3:
      return 'filled-tab--triple';
    default:
      return '';
  }
});

const setActive = (id: string) => {
  if (id === activeId.value) return;
  if (!normalizedTabs.value.some((tab) => tab.id === id)) return;
  if (props.beforeChange?.(id) === false) return;
  internalId.value = id;
  emit('update:modelValue', id);
  emit('change', id);
};
</script>

<style scoped lang="scss">
.filled-tab {
  --filled-tab-label-size: 16px;
  color: #1e1e1e;
  width: 100%;
  min-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 0 12px 12px 12px;

  &--triple {
    --filled-tab-label-size: 14px;
  }

  &__tabs {
    display: flex;
    align-items: flex-end;
    gap: 0;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  &__tab {
    background-color: #e5e5e5;
    color: #545454;
    border: none;
    border-radius: 12px 12px 0 0;
    padding: 14px 20px;
    font-size: var(--filled-tab-label-size);
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.2px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
    min-width: 140px;
    margin-right: -1px;
  }

  &__tab.is-active {
    background-color: #005d9a;
    color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }

  &__panel {
    width: 100%;
    background-color: #005d9a;
    border-radius: 0 12px 12px 12px;
    border: 1px solid #005d9a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-top: -1px;
  }

  &__placeholder {
    border-radius: 12px;
    background-color: #f9fafb;
    border: 1px dashed #cbd5e1;
    padding: 24px;
    text-align: left;
  }

  &__placeholder-title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 700;
    color: #101828;
  }

  &__placeholder-text {
    margin: 0;
    font-size: 14px;
    color: #475467;
  }

  &--double,
  &--triple {
    &__tab {
      border-radius: 14px 14px 0 0;
    }
  }

  &--mobile-full &__tabs {
    width: 100%;
  }

  @media (max-width: 768px) {
    &__tabs {
      gap: 0;
    }

    &--mobile-full &__tab {
      flex: 1;
      min-width: 0;
      border-radius: 14px 14px 0 0;
      padding: 14px 8px;
      margin-right: 0;
    }

    &--double &__label {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.5px;
      font-weight: 600;
    }

    &--triple &__label {
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.3px;
      font-weight: 500;
    }

    &__panel {
      padding: 16px;
      border-radius: 0;
    }
  }
}
</style>
